import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Buildings, Plus, ArrowRight, CalendarBlank, LinkSimple, PencilSimple, Trash, Ticket, X, MapPin } from "@phosphor-icons/react";

const ACT_LABEL = {
  prospect_email: "Prospect email", follow_up_email: "Follow-up email", warm_call: "Warm call",
  cold_call: "Cold call", proposal_sent: "Proposal sent", property_tour: "Property tour",
  contract_sent: "Contract sent", block_created: "Block created", beo_created: "BEO created",
  note: "Note", stage: "Stage change",
};

const BOOKING_STATUSES = [
  { value: "tentative", label: "Tentative", cls: "bg-[#FEF3C7] text-[#B45309]" },
  { value: "definite", label: "Definite", cls: "bg-[#E0E7FF] text-[#002FA7]" },
  { value: "completed", label: "Completed", cls: "bg-[#D1FAE5] text-[#047857]" },
  { value: "cancelled", label: "Cancelled", cls: "bg-[#F3F4F6] text-[#6B7280] line-through" },
];
const statusMeta = (s) => BOOKING_STATUSES.find((x) => x.value === s) || BOOKING_STATUSES[0];
const inputCls = "w-full border border-[#E5E7EB] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const emptyBooking = { name: "", hotel_id: "", contact_name: "", check_in: "", check_out: "", rooms: "", room_nights: "", rate: "", value: "", status: "tentative", po_number: "", notes: "" };

export function BookingForm({ accountId, properties, initial, onDone, defaultHotelId = "" }) {
  const qc = useQueryClient();
  const [f, setF] = useState(initial || { ...emptyBooking, hotel_id: defaultHotelId });
  const S = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const isEdit = !!(initial && initial.id);

  // live auto-calculation: room-nights = nights × rooms, total = rate × room-nights
  const nights = React.useMemo(() => {
    if (!f.check_in || !f.check_out) return 0;
    const ms = new Date(f.check_out + "T00:00:00") - new Date(f.check_in + "T00:00:00");
    const n = Math.round(ms / 86400000);
    return n > 0 ? n : 0;
  }, [f.check_in, f.check_out]);

  const setDates = (k) => (e) => {
    const v = e.target.value;
    setF((s) => {
      const next = { ...s, [k]: v };
      const ci = k === "check_in" ? v : s.check_in;
      const co = k === "check_out" ? v : s.check_out;
      if (ci && co) {
        const n = Math.round((new Date(co + "T00:00:00") - new Date(ci + "T00:00:00")) / 86400000);
        if (n > 0) {
          next.room_nights = String(n * (Number(s.rooms) || 1));
          if (Number(s.rate)) next.value = String(Math.round(Number(s.rate) * n * (Number(s.rooms) || 1)));
        }
      }
      return next;
    });
  };
  const setRooms = (e) => {
    const v = e.target.value;
    setF((s) => {
      const next = { ...s, rooms: v };
      if (nights > 0) {
        next.room_nights = String(nights * (Number(v) || 1));
        if (Number(s.rate)) next.value = String(Math.round(Number(s.rate) * nights * (Number(v) || 1)));
      }
      return next;
    });
  };
  const setRate = (e) => {
    const v = e.target.value;
    setF((s) => ({ ...s, rate: v, value: (Number(v) && Number(s.room_nights)) ? String(Math.round(Number(v) * Number(s.room_nights))) : s.value }));
  };
  const setRoomNights = (e) => {
    const v = e.target.value;
    setF((s) => ({ ...s, room_nights: v, value: (Number(s.rate) && Number(v)) ? String(Math.round(Number(s.rate) * Number(v))) : s.value }));
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name.trim(), hotel_id: f.hotel_id || "", contact_name: f.contact_name || "",
        check_in: f.check_in || "", check_out: f.check_out || "", rooms: Number(f.rooms) || 0,
        room_nights: Number(f.room_nights) || 0, rate: Number(f.rate) || 0, value: Number(f.value) || 0,
        status: f.status, po_number: f.po_number || "", notes: f.notes || "",
        source_lead_id: f.source_lead_id || "",
      };
      return isEdit ? api.updateBooking(accountId, initial.id, body) : api.createBooking(accountId, body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["account", accountId] }); qc.invalidateQueries({ queryKey: ["accounts"] }); qc.invalidateQueries({ queryKey: ["all-bookings"] }); toast.success(isEdit ? "Booking updated" : "Booking added"); onDone(); },
    onError: (e) => toast.error(errMsg(e, "Could not save booking")),
  });
  return (
    <div className="border border-[#002FA7] bg-[#F8FAFF] p-3 space-y-2" data-testid="booking-form">
      <div className="flex items-center justify-between">
        <span className="overline text-[#002FA7]">{isEdit ? "Edit booking" : "New booking"}</span>
        <button data-testid="booking-form-cancel" onClick={onDone} className="text-[#6B7280] hover:text-[#DC2626]"><X size={16} weight="bold" /></button>
      </div>
      <input data-testid="booking-name" value={f.name} onChange={S("name")} placeholder="Booking name (e.g. Annual Sales Conference 2027)" className={inputCls} />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <select data-testid="booking-hotel" value={f.hotel_id} onChange={S("hotel_id")} className={`${inputCls} bg-white`}>
            <option value="">— Property —</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {(() => {
            const sp = properties.find((p) => p.id === f.hotel_id);
            const loc = sp ? [sp.city, sp.state].filter(Boolean).join(", ") : "";
            return loc ? <div className="flex items-center gap-1 text-xs text-[#047857] mt-1" data-testid="booking-location-hint"><MapPin size={12} weight="fill" /> {loc}</div> : null;
          })()}
        </div>
        <select data-testid="booking-status" value={f.status} onChange={S("status")} className={`${inputCls} bg-white`}>
          {BOOKING_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <label className="text-xs text-[#4B5563]">Arrival<input data-testid="booking-checkin" type="date" value={f.check_in} onChange={setDates("check_in")} className={inputCls} /></label>
        <label className="text-xs text-[#4B5563]">Departure<input data-testid="booking-checkout" type="date" value={f.check_out} onChange={setDates("check_out")} className={inputCls} /></label>
        <label className="text-xs text-[#4B5563]"># Rooms<input data-testid="booking-rooms" type="number" value={f.rooms} onChange={setRooms} className={inputCls} /></label>
        <label className="text-xs text-[#4B5563]">Room-nights {nights > 0 && <span className="text-[#9CA3AF]">({nights} nt{nights > 1 ? "s" : ""})</span>}<input data-testid="booking-roomnights" type="number" value={f.room_nights} onChange={setRoomNights} className={inputCls} /></label>
        <label className="text-xs text-[#4B5563]">Rate $ / night<input data-testid="booking-rate" type="number" value={f.rate} onChange={setRate} className={inputCls} /></label>
        <label className="text-xs text-[#4B5563]">Total value $ <span className="text-[#047857] font-semibold">(auto)</span><input data-testid="booking-value" type="number" value={f.value} onChange={S("value")} className={`${inputCls} font-semibold`} /></label>
        <input data-testid="booking-contact" value={f.contact_name} onChange={S("contact_name")} placeholder="Contact name" className={inputCls} />
        <input data-testid="booking-po" value={f.po_number} onChange={S("po_number")} placeholder="PO / reference #" className={inputCls} />
      </div>
      <textarea data-testid="booking-notes" rows={2} value={f.notes} onChange={S("notes")} placeholder="Notes" className={inputCls} />
      <button data-testid="booking-save" disabled={!f.name.trim() || save.isPending} onClick={() => save.mutate()}
        className="bg-[#002FA7] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#00227A] disabled:opacity-60">
        {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Add booking"}
      </button>
    </div>
  );
}

function AccountDetail({ id, onClose }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["account", id], queryFn: () => api.account(id), enabled: !!id });
  const { data: properties = [] } = useQuery({ queryKey: ["my-properties"], queryFn: () => api.myProperties() });
  const [formFor, setFormFor] = useState(null); // null | 'new' | booking object
  const copyLink = useMutation({
    mutationFn: () => api.createIntakeLink({ account_id: id }),
    onSuccess: (res) => {
      const url = `${window.location.origin}/intake/${res.token}`;
      navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Requirement link copied — send it to this account");
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delBooking = useMutation({
    mutationFn: (bid) => api.deleteBooking(id, bid),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["account", id] }); qc.invalidateQueries({ queryKey: ["accounts"] }); toast.success("Booking removed"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!data) return <div className="p-6 text-sm text-[#4B5563]">Loading…</div>;
  const { account, leads, bookings = [], timeline, totals } = data;
  const defaultHotelId = (leads.find((l) => l.hotel_id)?.hotel_id) || (properties.length === 1 ? properties[0].id : "");
  return (
    <div data-testid="account-detail">
      <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Buildings size={20} weight="bold" color="#002FA7" /> {account.name}</DialogTitle></DialogHeader>
      <button data-testid="account-copy-link" onClick={() => copyLink.mutate()} disabled={copyLink.isPending}
        className="mt-1 inline-flex items-center gap-1.5 border border-[#002FA7] text-[#002FA7] px-3 py-1.5 text-sm font-semibold hover:bg-[#EEF2FF] transition-colors disabled:opacity-60">
        <LinkSimple size={14} weight="bold" /> {copyLink.isPending ? "Generating…" : "Copy requirement link"}
      </button>
      <div className="grid grid-cols-4 gap-2 mt-2">
        <div className="bg-[#F3F4F6] px-3 py-2"><div className="overline text-[#4B5563]">Bookings</div><div className="font-display font-black text-xl">{totals.bookings || 0}</div></div>
        <div className="bg-[#D1FAE5] px-3 py-2"><div className="overline text-[#4B5563]">Booking value</div><div className="font-display font-black text-xl text-[#047857]">{money(totals.bookings_value || 0)}</div></div>
        <div className="bg-[#F3F4F6] px-3 py-2"><div className="overline text-[#4B5563]">Leads</div><div className="font-display font-black text-xl">{totals.leads}</div></div>
        <div className="bg-[#E0E7FF] px-3 py-2"><div className="overline text-[#4B5563]">Room-nights</div><div className="font-display font-black text-xl text-[#002FA7]">{totals.room_nights}</div></div>
      </div>

      {/* Bookings subsection */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <span className="overline text-[#4B5563] flex items-center gap-1.5"><Ticket size={14} weight="bold" color="#002FA7" /> Bookings</span>
          {formFor === null && (
            <button data-testid="booking-add-btn" onClick={() => setFormFor("new")}
              className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white px-3 py-1.5 text-sm font-semibold hover:bg-[#00227A]"><Plus size={14} weight="bold" /> Add booking</button>
          )}
        </div>
        {formFor === "new" && <BookingForm accountId={id} properties={properties} defaultHotelId={defaultHotelId} onDone={() => setFormFor(null)} />}
        <div className="space-y-2 mt-2">
          {bookings.map((b) => (
            formFor && formFor.id === b.id ? (
              <BookingForm key={b.id} accountId={id} properties={properties} initial={b} onDone={() => setFormFor(null)} />
            ) : (
              <div key={b.id} data-testid={`booking-${b.id}`} className="border border-[#E5E7EB] px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#111827]">{b.name}</span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 ${statusMeta(b.status).cls}`}>{statusMeta(b.status).label}</span>
                    </div>
                    <div className="text-sm text-[#4B5563] mt-0.5">
                      {b.hotel_name || "—"}{b.location ? <span className="text-[#047857]"> · {b.location}</span> : null}
                      {(b.check_in || b.check_out) && <> · {b.check_in}{b.check_out ? `–${b.check_out}` : ""}</>}
                      {b.rooms ? <> · {b.rooms} rooms</> : null}
                      {b.room_nights ? <> · {b.room_nights} rm-nts</> : null}
                    </div>
                    {(b.contact_name || b.po_number) && <div className="text-xs text-[#9CA3AF] mt-0.5">{[b.contact_name, b.po_number && `PO ${b.po_number}`].filter(Boolean).join(" · ")}</div>}
                    {b.notes && <div className="text-xs text-[#6B7280] mt-0.5 italic">“{b.notes}”</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-display font-black text-[#111827]">{money(b.value)}</span>
                    <button data-testid={`booking-edit-${b.id}`} onClick={() => setFormFor(b)} className="text-[#4B5563] hover:text-[#002FA7]"><PencilSimple size={15} weight="bold" /></button>
                    <button data-testid={`booking-delete-${b.id}`} onClick={() => delBooking.mutate(b.id)} className="text-[#4B5563] hover:text-[#DC2626]"><Trash size={15} weight="bold" /></button>
                  </div>
                </div>
              </div>
            )
          ))}
          {bookings.length === 0 && formFor !== "new" && <p className="text-sm text-[#4B5563]" data-testid="bookings-empty">No bookings yet. Add one to start tracking business separately.</p>}
        </div>
      </div>

      <div className="mt-5">
        <span className="overline text-[#4B5563] block mb-2">Leads / enquiries</span>
        <div className="space-y-1">
          {leads.map((l) => (
            <Link key={l.id} to={`/leads/${l.id}`} onClick={onClose} data-testid={`account-lead-${l.id}`}
              className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm hover:border-[#002FA7]">
              <span className="font-semibold text-[#111827]">{l.company_name} <span className="text-[#9CA3AF] font-normal">· {l.hotel_name || "—"} · {l.status}</span></span>
              <ArrowRight size={14} className="text-[#9CA3AF]" />
            </Link>
          ))}
          {leads.length === 0 && <p className="text-sm text-[#4B5563]">No leads/enquiries yet.</p>}
        </div>
      </div>

      <div className="mt-5">
        <span className="overline text-[#4B5563] block mb-2">Activity timeline</span>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {timeline.map((a, i) => (
            <div key={i} className="text-xs text-[#4B5563] border-l-2 border-[#E5E7EB] pl-2 py-0.5">
              <span className="inline-block bg-[#EEF2FF] text-[#002FA7] px-1.5 py-0.5 font-semibold mr-1">{ACT_LABEL[a.kind] || a.kind || "Note"}</span>
              {a.text} <span className="text-[#9CA3AF]">· {a.by} · {(a.at || "").slice(0, 10)} · {a.company_name}</span>
            </div>
          ))}
          {timeline.length === 0 && <p className="text-sm text-[#4B5563]">No activity logged yet.</p>}
        </div>
      </div>
    </div>
  );
}

export default function AccountsPanel() {
  const qc = useQueryClient();
  const { data: properties = [] } = useQuery({ queryKey: ["my-properties"], queryFn: () => api.myProperties() });
  const [propFilter, setPropFilter] = useState("all");
  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts", propFilter],
    queryFn: () => api.accounts(propFilter === "all" ? {} : { hotel_id: propFilter }),
  });
  const [open, setOpen] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => api.createAccount({ name: name.trim(), type: "company" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["accounts"] }); toast.success("Account created"); setNewOpen(false); setName(""); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="space-y-4" data-testid="accounts-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-[#4B5563]">Companies you do business with. Track multiple <b>bookings</b> per account, plus leads &amp; activity, so repeat business stays organised.</p>
        <div className="flex items-center gap-2">
          {properties.length > 1 && (
            <select data-testid="accounts-property-filter" value={propFilter} onChange={(e) => setPropFilter(e.target.value)}
              className="border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">All properties</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button data-testid="account-new-btn" onClick={() => setNewOpen(true)} className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00227A]"><Plus size={15} weight="bold" /> New Account</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.map((a) => (
          <button key={a.id} data-testid={`account-card-${a.id}`} onClick={() => setOpen(a.id)}
            className="text-left border border-[#E5E7EB] p-4 hover:border-[#002FA7] transition-colors">
            <div className="flex items-center gap-1.5 font-display font-bold text-[#111827]">
              <Buildings size={15} weight="bold" color={a.type === "one_time" ? "#B45309" : "#002FA7"} /> {a.name}
              {a.type === "one_time" && <span className="text-[10px] bg-[#FEF3C7] text-[#B45309] px-1.5 py-0.5 font-semibold uppercase">One-time</span>}
            </div>
            <div className="text-sm text-[#4B5563] mt-1">{a.bookings || 0} bookings · {a.leads} leads · {a.room_nights} rm-nts</div>
            <div className="flex items-center justify-between mt-1">
              <span className="font-display font-black text-[#111827]">{money((a.bookings_value || 0) + (a.revenue || 0))}</span>
              {a.last_activity && <span className="text-xs text-[#9CA3AF] inline-flex items-center gap-1"><CalendarBlank size={12} /> {a.last_activity.slice(0, 10)}</span>}
            </div>
          </button>
        ))}
        {accounts.length === 0 && <p className="text-sm text-[#4B5563]">No accounts yet. Add a lead or create one.</p>}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="rounded-none border-[#111827] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="sr-only">Account details</DialogTitle>
          {open && <AccountDetail id={open} onClose={() => setOpen(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none border-[#111827] max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">New account</DialogTitle></DialogHeader>
          <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Company / account name</span>
            <input data-testid="account-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Traveling Teams" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <button data-testid="account-create-btn" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()} className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] disabled:opacity-60">{create.isPending ? "Creating…" : "Create account"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
